/**
 * Cache global des SETS de composants (référence publique). Chargé une seule
 * fois puis indexé par id — utilisé par le tooltip d'objet (encart « Set ») et
 * la section Bibliothèque. Évite de refetch à chaque survol.
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { fetchLibraryComponentSets } from '@/api/library';
import type { LibraryComponentSet } from '@/types/library';

export const useComponentSetStore = defineStore('componentSet', () => {
  const all = ref<LibraryComponentSet[]>([]);
  const byId = ref<Record<number, LibraryComponentSet>>({});
  const loaded = ref(false);
  let loading: Promise<void> | null = null;

  function ensureLoaded(): Promise<void> {
    if (loaded.value) return Promise.resolve();
    if (loading) return loading;
    loading = (async () => {
      try {
        const sets = await fetchLibraryComponentSets();
        all.value = sets;
        byId.value = Object.fromEntries(sets.map((s) => [s.id, s]));
        loaded.value = true;
      } catch {
        /* non bloquant : pas d'encart de set si l'appel échoue */
      } finally {
        loading = null;
      }
    })();
    return loading;
  }

  /** Set par id (null si inconnu / pas encore chargé). */
  function get(id: number | null | undefined): LibraryComponentSet | null {
    return id != null ? (byId.value[id] ?? null) : null;
  }

  return { all, byId, loaded, ensureLoaded, get };
});
